"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        caption: "flex justify-center pt-1 relative items-center w-full z-10",
        caption_label: "text-sm font-medium text-white",
        nav: "flex items-center gap-1",
        nav_button: cn(
          "size-7 glass border border-white/10 p-0 text-white/60 hover:text-white hover:border-warning/30 transition-colors rounded-md",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-x-1",
        head_row: "flex",
        head_cell:
          "text-white/40 rounded-md w-10 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-warning/20 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md",
        ),
        day: cn(
          "size-10 p-0 font-normal text-white/70 hover:text-white hover:bg-white/5 rounded-md transition-colors aria-selected:opacity-100",
        ),
        day_range_start:
          "day-range-start aria-selected:bg-warning/30 aria-selected:text-warning",
        day_range_end:
          "day-range-end aria-selected:bg-warning/30 aria-selected:text-warning",
        day_selected:
          "bg-warning/30 text-warning hover:bg-warning/40 hover:text-warning focus:bg-warning/30 focus:text-warning border border-warning/30",
        day_today: "bg-white/5 text-white border border-white/20",
        day_outside:
          "day-outside text-white/20 aria-selected:text-white/30",
        day_disabled: "text-white/20 opacity-50",
        day_range_middle:
          "aria-selected:bg-warning/20 aria-selected:text-warning",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("size-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("size-4", className)} {...props} />
        ),
      }}
      {...props}
    />
  );
}

export { Calendar };